/**
 * A lazy chunk failed to load — almost always because the server was rebuilt
 * under this tab and the chunk's hashed name is gone from disk (2026-09-10).
 * Recovery: wait until the server answers, THEN reload; never reload into an
 * outage, and never more than a few times per tab, so a real outage or a
 * genuinely broken build cannot loop. The previous once-per-session guard
 * stranded a tab whose single reload fell inside a restart window: a white
 * page and a composer saying "reconnecting", with nothing left to try.
 */
export const CHUNK_RELOAD_WINDOW_MS = 10 * 60 * 1000;
export const CHUNK_RELOAD_MAX = 3;
export const CHUNK_WAIT_MS = 5 * 60 * 1000;
export const CHUNK_POLL_MS = 2000;
const KEY = "agnt-chunk-reloads";

export interface RecoveryDeps {
	fetchHealth: () => Promise<boolean>;
	reload: () => void;
	storage: Pick<Storage, "getItem" | "setItem"> | null;
	now: () => number;
	sleep: (ms: number) => Promise<void>;
}

export type RecoveryOutcome = "reloaded" | "gave_up" | "server_down";

function recentReloads(storage: RecoveryDeps["storage"], now: number): number[] {
	try {
		const raw = storage?.getItem(KEY);
		const list = raw ? (JSON.parse(raw) as unknown) : [];
		return Array.isArray(list) ? list.filter((t): t is number => typeof t === "number" && t > now - CHUNK_RELOAD_WINDOW_MS) : [];
	} catch {
		return [];
	}
}

export async function recoverFromChunkFailure(deps: RecoveryDeps = defaultDeps()): Promise<RecoveryOutcome> {
	const times = recentReloads(deps.storage, deps.now());
	if (times.length >= CHUNK_RELOAD_MAX) return "gave_up";
	const deadline = deps.now() + CHUNK_WAIT_MS;
	while (!(await deps.fetchHealth())) {
		if (deps.now() >= deadline) return "server_down";
		await deps.sleep(CHUNK_POLL_MS);
	}
	try {
		deps.storage?.setItem(KEY, JSON.stringify([...times, deps.now()]));
	} catch {
		/* storage unavailable — the reload still happens, unguarded is better than stranded */
	}
	deps.reload();
	return "reloaded";
}

function defaultDeps(): RecoveryDeps {
	let storage: RecoveryDeps["storage"] = null;
	try {
		storage = window.sessionStorage;
	} catch {
		storage = null;
	}
	return {
		fetchHealth: async () => {
			try {
				const res = await fetch("/api/health", { cache: "no-store", credentials: "same-origin" });
				return res.ok;
			} catch {
				return false;
			}
		},
		reload: () => window.location.reload(),
		storage,
		now: () => Date.now(),
		sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
	};
}
